import { useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import {
  ListingApplicationState,
  ListingApplicationsDocument,
  SortOrder,
  type ListingApplicationRowFragment,
} from "@/graphql/generated/operations";
import { usePagedQuery } from "@/lib/use-paged-query";
import { LISTING_APPLICATION_STATE_LABELS } from "@/pages/share/applications/listing-application-state";
import type { ToMatchPerson } from "@/pages/project/screens/ProjectToMatchScreen";

// Wer aus den Bewerbungen einer Ausschreibung in ihr Match gehen kann: wessen Bewerbung eingegangen,
// in Prüfung oder angenommen ist. Abgelehnte und Zurückgezogene stehen nicht mehr zur Wahl
// (@external-adr GENESIS-0087-application-phases-are-separate-from-decisions (GENESIS-0071 §3)).
//
// Die ganze Liste, nicht die erste Seite: wer hier "alle" mitnimmt, darf nicht still die Bewerbenden
// ab Seite zwei verlieren. Bis sie vollständig ist, meldet der Hook `loading`.

const CANDIDATE_STATES: readonly ListingApplicationState[] = [
  ListingApplicationState.Received,
  ListingApplicationState.InReview,
  ListingApplicationState.Accepted,
];

export function useListingApplicants(listingId: string, { skip }: { skip: boolean }) {
  const intl = useIntl();
  const input = useMemo(
    () => ({
      order: { receivedOn: SortOrder.Desc },
      // genesis' FilterSpec wertet nur das ERSTE gesetzte Feld aus und verwirft weitere still, und
      // `in` kennt sein Enum-Filter nicht: Ausschreibung und Zustände gehen über `and` und `or`.
      where: {
        and: [{ listingId: { eq: listingId } }, { or: CANDIDATE_STATES.map((state) => ({ state: { eq: state } })) }],
      },
    }),
    [listingId],
  );
  const { nodes, loading, error, hasMore, loadingMore, loadMoreFailed, loadMore } =
    usePagedQuery<ListingApplicationRowFragment>(ListingApplicationsDocument, {
      connectionKey: "listingApplications",
      input,
      pageSize: 100,
      skip,
    });

  useEffect(() => {
    if (hasMore && !loadingMore && !loadMoreFailed) loadMore();
  }, [hasMore, loadingMore, loadMoreFailed, loadMore]);

  const people = useMemo(() => {
    const seen = new Set<string>();
    const result: ToMatchPerson[] = [];
    for (const application of nodes) {
      const person = application.person;
      // Der Zustand noch einmal hier: verwürfe genesis den Filter still, ginge sonst eine Absage mit.
      if (application.companyId || !person || seen.has(person.id) || !CANDIDATE_STATES.includes(application.state)) continue;
      seen.add(person.id);
      result.push({
        id: person.id,
        name:
          person.formattedName ||
          intl.formatMessage({ id: "share.listings.applications.unnamedPerson", defaultMessage: "Person ohne Namen" }),
        avatarContent: person.avatar?.content,
        avatarMime: person.avatar?.mimeType,
        note: intl.formatMessage(LISTING_APPLICATION_STATE_LABELS[application.state]),
      });
    }
    return result;
  }, [intl, nodes]);

  const companies = useMemo(() => [...new Map(nodes.filter((application) => application.companyId
      && CANDIDATE_STATES.includes(application.state)).map((application) => [application.companyId!,
        { id: application.companyId!, name: application.companyName ?? "" }])).values()], [nodes]);
  return {
    companies,
    people,
    loading: !skip && (loading || (hasMore && !loadMoreFailed)),
    // Eine Folgeseite, die nicht kam, ist eine unvollständige Liste und wird wie ein Fehler gemeldet.
    error: error ?? (loadMoreFailed ? new Error("listingApplications: next page failed") : undefined),
  };
}
